import { useQuery } from "@tanstack/react-query";
import { fetchApiGet } from "../utils";

export interface ActiveStatusCoordinationCount {
  coordination_type: string;
  status: string;
  count: number;
}

export interface ActiveStatusCoordinationStats {
  counts_by_coordination_type_and_status: ActiveStatusCoordinationCount[];
  older_than_two_hours_counts_by_coordination_type_and_status: ActiveStatusCoordinationCount[];
  expired_counts_by_coordination_type_and_status: ActiveStatusCoordinationCount[];
}

export interface ActiveQueueStats {
  queue_id: number;
  name: string;
  pending: number;
  processing: number;
  failed: number;
  halted: number;
  earliest_halted_at: string | null;
  latest_halted_at: string | null;
}

export interface StuckContractDispatchCount {
  count: number;
}

export type StuckAgtechContractDispatchCount = StuckContractDispatchCount;
export type StuckOtcContractDispatchCount = StuckContractDispatchCount;

export type AgtechProxySyncFlagType =
  | "agtech_trigger_sync_override_halt"
  | "agtech_trigger_sync_last_halt_status";

export interface AgtechProxySyncFlag {
  flag_type: AgtechProxySyncFlagType;
  flag_status: boolean;
  enabled: boolean;
  last_updated: string;
}

const staticQueryOptions = {
  retry: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  refetchOnMount: false,
} as const;

export function useActiveStatusCoordinationStats() {
  return useQuery({
    queryKey: ["developer", "system", "active-status-coordination", "stats"],
    queryFn: () => fetchApiGet<ActiveStatusCoordinationStats>(
      "developer/system/active-status-coordination/stats",
    ),
    ...staticQueryOptions,
  });
}

export function useStuckAgtechContractDispatchCount() {
  return useQuery({
    queryKey: ["developer", "system", "documents", "agtech", "contracts", "stuck-dispatch-count"],
    queryFn: () => fetchApiGet<StuckAgtechContractDispatchCount>(
      "developer/system/documents/agtech/contracts/stuck-dispatch-count",
    ),
    ...staticQueryOptions,
  });
}

export function useStuckOtcContractDispatchCount() {
  return useQuery({
    queryKey: ["developer", "system", "documents", "otc", "contracts", "stuck-dispatch-count"],
    queryFn: () => fetchApiGet<StuckOtcContractDispatchCount>(
      "developer/system/documents/otc/contracts/stuck-dispatch-count",
    ),
    ...staticQueryOptions,
  });
}

export function useAgtechProxySyncFlags() {
  return useQuery({
    queryKey: ["developer", "system", "agtech", "proxy", "sync", "flags"],
    queryFn: () => fetchApiGet<AgtechProxySyncFlag[]>(
      "developer/system/agtech/proxy/sync/flags",
    ),
    ...staticQueryOptions,
  });
}

export function useActivePgJobQueueStats() {
  return useQuery({
    queryKey: ["developer", "system", "pg-job-queues", "stats"],
    queryFn: () => fetchApiGet<ActiveQueueStats[]>(
      "developer/system/pg-job-queues/stats",
    ),
    ...staticQueryOptions,
  });
}
